import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../config';
import { paintInteriorBackdrop } from '../village/art';
import type { VillageBuildingId } from '../village/buildings';
import {
  addBackToVillageButton,
  titleBlock,
} from '../ui/hubUi';

export interface ComingSoonSceneData {
  buildingId: VillageBuildingId;
  title: string;
}

/**
 * Polished full-screen "coming soon" interiors for Tavern / Shrine.
 * Buildings stay visible and tappable — not silently functional.
 */
export class ComingSoonScene extends Phaser.Scene {
  private buildingId: VillageBuildingId = 'tavern';
  private buildingTitle = 'Coming Soon';

  constructor() {
    super('ComingSoon');
  }

  init(data: ComingSoonSceneData): void {
    this.buildingId = data.buildingId ?? 'tavern';
    this.buildingTitle = data.title ?? 'Coming Soon';
  }

  create(): void {
    const cx = GAME_WIDTH / 2;
    const isTavern = this.buildingId === 'tavern';
    const wall = isTavern ? 0x4a2228 : 0x2a2448;
    const floor = isTavern ? 0x2e181c : 0x1a1630;
    const g = this.add.graphics();
    paintInteriorBackdrop(g, GAME_WIDTH, GAME_HEIGHT, wall, floor);

    if (isTavern) {
      // Tables / warm lamps
      const lamp = this.add.circle(cx - 60, 200, 14, 0xffc857, 0.85);
      const lamp2 = this.add.circle(cx + 60, 210, 12, 0xffc857, 0.7);
      this.tweens.add({
        targets: [lamp, lamp2],
        alpha: 0.4,
        duration: 700,
        yoyo: true,
        repeat: -1,
      });
      this.add
        .rectangle(cx, 250, 160, 40, 0x5a4030)
        .setStrokeStyle(2, 0x3a2a20);
    } else {
      // Shrine pedestal glow
      const glow = this.add.circle(cx, 220, 36, 0xe8d5a3, 0.25);
      this.tweens.add({
        targets: glow,
        scale: 1.3,
        alpha: 0.45,
        duration: 1600,
        yoyo: true,
        repeat: -1,
      });
      this.add.triangle(cx, 200, 0, 40, 30, -20, -30, -20, 0xb8a0e8);
    }

    addBackToVillageButton(this);
    titleBlock(
      this,
      this.buildingTitle,
      isTavern
        ? 'A hearth for rumours and rest — not open yet.'
        : 'Quiet stone and soft light — rites arrive later.',
      340,
    );

    this.add
      .text(cx, 430, 'COMING SOON', {
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '32px',
        color: '#e8d5a3',
        stroke: '#1a1408',
        strokeThickness: 4,
      })
      .setOrigin(0.5);

    this.add
      .text(
        cx,
        490,
        isTavern
          ? 'Tavern services are planned but not implemented.\nThis interior is intentionally non-functional.'
          : 'Shrine blessings are planned but not implemented.\nThis interior is intentionally non-functional.',
        {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '13px',
          color: '#9aa3c0',
          align: 'center',
          wordWrap: { width: GAME_WIDTH - 48 },
          lineSpacing: 6,
        },
      )
      .setOrigin(0.5);

    this.add
      .text(cx, GAME_HEIGHT - 56, 'Temporary illustrated placeholder', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '11px',
        color: '#6e7694',
      })
      .setOrigin(0.5);
  }
}
